/**
 * Client-side domain normalisation and validation.
 *
 * This mirrors the backend logic in `backend/src/lib/validate.ts` so the UI
 * can give instant feedback without a round-trip. Keep both copies in sync.
 */

const DOMAIN_LABEL_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const MAX_DOMAIN_LENGTH = 253;
const MAX_LABEL_LENGTH = 63;

/**
 * Normalise a user-entered string into a canonical domain form.
 *
 * - Trims whitespace
 * - Lowercases
 * - Strips `http://` or `https://` protocol prefixes
 * - Strips paths, query strings, and fragment identifiers
 * - Strips trailing dots
 * - Strips port numbers
 */
export function normalizeDomain(input: string): string {
  let domain = input.trim().toLowerCase();

  // Strip protocol
  domain = domain.replace(/^https?:\/\//, '');

  // Strip path, query, fragment
  domain = domain.replace(/[/?#].*$/, '');

  // Strip trailing dot
  domain = domain.replace(/\.$/, '');

  // Strip port
  domain = domain.replace(/:\d+$/, '');

  return domain;
}

/**
 * Validate a normalised domain string.
 *
 * @returns `{ valid: true }` or `{ valid: false, error: string }`.
 */
export function validateDomain(
  domain: string,
): { valid: boolean; error?: string } {
  if (domain.length === 0) {
    return { valid: false, error: 'Domain cannot be empty' };
  }

  if (domain.length > MAX_DOMAIN_LENGTH) {
    return {
      valid: false,
      error: `Domain exceeds maximum length of ${MAX_DOMAIN_LENGTH} characters`,
    };
  }

  const labels = domain.split('.');
  if (labels.length < 2) {
    return {
      valid: false,
      error: 'Domain must have at least two labels (e.g., example.com)',
    };
  }

  for (const label of labels) {
    if (label.length === 0) {
      return { valid: false, error: 'Domain contains empty label' };
    }
    if (label.length > MAX_LABEL_LENGTH) {
      return {
        valid: false,
        error: `Label "${label}" exceeds maximum length of ${MAX_LABEL_LENGTH} characters`,
      };
    }
    if (!DOMAIN_LABEL_REGEX.test(label)) {
      return {
        valid: false,
        error: `Label "${label}" contains invalid characters or starts/ends with a hyphen`,
      };
    }
  }

  return { valid: true };
}
